import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { IMAGEMOTION_PACKAGES } from '@rehub/pricing';
import SampleVideoDisclosure from './SampleVideoDisclosure';

const TESTER = IMAGEMOTION_PACKAGES.tester;

/** Example video shown at the top of the public entry page. */
const IMAGEMOTION_VIDEO = {
  title: 'ImageMotion example',
  youtubeId: 'lZxP4IsBzvE',
} as const;

const MORE_EXAMPLES_URL = 'https://www.imagemotion.ai/examples';
const STAGING_SITE_URL = 'https://staginganimation.com';

const REASSURANCE = `Up to ${TESTER.maxImages} photos · Small watermark · No card required`;

const buttonBase =
  'inline-flex w-full items-center justify-center min-h-11 px-5 rounded-lg text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#071422] sm:w-auto';

function ExampleVideo() {
  const [playing, setPlaying] = useState(false);
  // Local 1920×1080 frame. YouTube's preview is only 480px and looks soft when scaled.

  return (
    <figure className="mx-auto mt-5 w-full min-w-0 max-w-3xl sm:mt-6">
      <div className="relative aspect-video w-full min-w-0 overflow-hidden rounded-lg border border-slate-700 bg-slate-950">
        {playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${IMAGEMOTION_VIDEO.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={IMAGEMOTION_VIDEO.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex items-center justify-center text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300"
            aria-label={`Play video: ${IMAGEMOTION_VIDEO.title}`}
          >
            <img
              src="/demo-london.jpg"
              alt=""
              width={1920}
              height={1080}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-950/75 ring-1 ring-white/40">
              <Play size={22} className="ml-0.5" fill="currentColor" aria-hidden />
            </span>
          </button>
        )}
      </div>
      <figcaption className="mt-2 text-center text-sm text-slate-200">
        See ImageMotion in action
      </figcaption>
      <div className="mt-3 flex justify-center">
        <a
          href={MORE_EXAMPLES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`${buttonBase} border border-slate-400 text-white hover:border-slate-200 hover:bg-white/5`}
        >
          See more examples
        </a>
      </div>
    </figure>
  );
}

interface LandingPageProps {
  onSignUp: () => void;
  onLogin: () => void;
  onOpenStaging?: () => void;
  onOpenApp?: () => void;
  isAuthenticated?: boolean;
  onOpenPricing?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onSignUp,
  onLogin,
  onOpenStaging,
  onOpenApp,
  isAuthenticated = false,
  onOpenPricing,
}) => {
  return (
    <main className="relative z-10 overflow-x-clip">
      <div className="container mx-auto px-4 pt-6 pb-12 sm:pt-8 sm:pb-14 lg:pt-8 lg:pb-10">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="mx-auto max-w-[22rem] text-[1.875rem] font-semibold leading-[1.15] tracking-tight text-balance text-white sm:max-w-2xl sm:text-[2.125rem] lg:max-w-[14em] lg:text-[3.25rem]">
            Turn property photos into cinematic videos
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
            Create engaging property videos from the photos you already have, ready for listings, websites and social media.
          </p>

          <div className="mx-auto mt-5 flex max-w-md flex-col items-stretch justify-center gap-3 sm:mt-6 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center">
            {isAuthenticated ? (
              <button type="button" onClick={onOpenApp} className={`${buttonBase} bg-blue-600 text-white hover:bg-blue-500`}>
                Open ImageMotion
              </button>
            ) : (
              <>
                <button type="button" onClick={onSignUp} className={`${buttonBase} bg-blue-600 text-white hover:bg-blue-500`}>
                  Create your free sample video
                </button>
                <button
                  type="button"
                  onClick={onLogin}
                  className={`${buttonBase} border border-slate-400 text-white hover:border-slate-200 hover:bg-white/5`}
                >
                  Sign in
                </button>
              </>
            )}
            {onOpenPricing && (
              <button
                type="button"
                onClick={onOpenPricing}
                className={`${buttonBase} text-slate-100 underline-offset-4 hover:underline`}
              >
                Pricing
              </button>
            )}
          </div>

          {!isAuthenticated && (
            <div className="mt-3">
              <p className="text-balance text-sm text-slate-200">{REASSURANCE}</p>
              <SampleVideoDisclosure idPrefix="entry" />
            </div>
          )}

          <ExampleVideo />

          <div className="mx-auto mt-10 w-full max-w-md border-t border-white/10 pt-8 text-center">
            <a
              href={STAGING_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#071422]"
            >
              <img
                src="/staging-animation-logo.png"
                alt="Staging Animation by ImageMotion"
                width={817}
                height={122}
                className="mx-auto h-8 w-auto max-w-full object-contain sm:h-11"
              />
            </a>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-200">
              Animate an empty room into a fully staged space.
            </p>
            {onOpenStaging && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={onOpenStaging}
                  className={`${buttonBase} border border-slate-400 text-white hover:border-slate-200 hover:bg-white/5`}
                >
                  {isAuthenticated ? 'Open Staging Animation' : 'Create a staging animation'}
                </button>
              </div>
            )}
            <a
              href={STAGING_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block text-sm font-medium text-cyan-200 underline underline-offset-4 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded"
            >
              staginganimation.com
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default LandingPage;
